import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Table } from 'src/design/molecules/Table';
import React, { useId } from 'react';
import {
  AdminStatsOverviewDto,
  AdminToolStatsDto,
} from 'src/api/FiretrolApiClient';
import {
  IStatSectionMetric,
  StatSectionCard,
} from 'src/design/molecules/StatSectionCard';
import {
  countLabel,
  formatDurationMs,
} from 'src/routes/Settings/AIAssistant/replyUsage';
import { formatNumber, formatPercent, humanFileSize } from 'src/utils';

export interface IToolUseCardProps {
  overview: AdminStatsOverviewDto | null;
  loading: boolean;
}

const roundsFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

interface IToolStatsTableProps {
  rows: AdminToolStatsDto[];
}

const ToolStatsTable: React.FC<IToolStatsTableProps> = ({ rows }) => {
  const titleId = useId();
  const hintId = useId();

  return (
    <Stack gap="xs">
      <Stack gap={2}>
        <Text id={titleId} size="xs" fw={600} tt="uppercase" c="gray.7">
          By tool
        </Text>
        <Text id={hintId} size="xs" c="gray.7">
          Errors, error rate, and durations cover only calls that ran. Duplicate
          and capped calls count in Calls and Avg result but do not run.
        </Text>
      </Stack>
      <Table.ScrollContainer minWidth={640}>
        <Table
          aria-labelledby={titleId}
          aria-describedby={hintId}
          fz="sm"
          verticalSpacing={4}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tool</Table.Th>
              <Table.Th ta="right">Calls</Table.Th>
              <Table.Th ta="right">Errors</Table.Th>
              <Table.Th ta="right">Error rate</Table.Th>
              <Table.Th ta="right">Avg duration</Table.Th>
              <Table.Th ta="right">p95 duration</Table.Th>
              <Table.Th ta="right">Avg result</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.toolName}>
                <Table.Td>{row.toolName}</Table.Td>
                <Table.Td ta="right">{formatNumber(row.callCount)}</Table.Td>
                <Table.Td ta="right">{formatNumber(row.errorCount)}</Table.Td>
                <Table.Td ta="right">{formatPercent(row.errorRate)}</Table.Td>
                <Table.Td ta="right">
                  {formatDurationMs(row.avgDurationMs)}
                </Table.Td>
                <Table.Td ta="right">
                  {formatDurationMs(row.p95DurationMs)}
                </Table.Td>
                <Table.Td ta="right">
                  {humanFileSize(row.avgResultBytes)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
};

export const ToolUseCard: React.FC<IToolUseCardProps> = ({
  overview,
  loading,
}) => {
  const toolUse = overview?.toolUse;
  const tools = overview?.tools ?? [];
  const avgRounds = toolUse?.avgRoundsPerReplyWithTools;

  const metrics: IStatSectionMetric[] = [
    {
      label:
        toolUse?.repliesWithToolCalls === 1
          ? 'reply used tools'
          : 'replies used tools',
      value: formatNumber(toolUse?.repliesWithToolCalls),
    },
    {
      label: 'avg rounds per reply with tools',
      value: avgRounds == null ? '—' : roundsFormatter.format(avgRounds),
    },
    {
      label: 'of replies hit the tool limit',
      value: formatPercent(toolUse?.toolRoundLimitShare),
    },
    {
      label: countLabel(toolUse?.duplicateCallCount, 'duplicate call'),
      value: formatNumber(toolUse?.duplicateCallCount),
    },
    {
      label: countLabel(toolUse?.cappedCallCount, 'capped call'),
      value: formatNumber(toolUse?.cappedCallCount),
    },
  ];

  return (
    <StatSectionCard
      title="Tool use"
      value={formatNumber(toolUse?.callCount)}
      valueLabel={countLabel(toolUse?.callCount, 'call')}
      hint={
        toolUse?.callCount === 0 ? 'No tool calls in this range' : undefined
      }
      metrics={metrics}
      loading={loading}
    >
      {!loading && tools.length > 0 && <ToolStatsTable rows={tools} />}
    </StatSectionCard>
  );
};
